import { useState } from "react";
import { ADD_COMMENT } from "../mutations/comment";
import {
  UPDATE_REFERRAL,
  SEND_REFERRAL_EVENT_MESSAGE,
} from "../mutations/referrals";

import { useMutation } from "@apollo/client";
import { useDispatch } from "react-redux";
import { enqueueSnackbar } from "../state/ducks/notifiers";
import { addCommentToReferralSuccess } from "../state/ducks/activity";
import { resetFormCache } from "../state/ducks/form";
import { changeReferralStatusSuccess as changeReferralStatusSuccessReceived } from "../state/ducks/referralsReceived";

import { changeReferralStatusSuccess as changeReferralStatusSuccessSent } from "../state/ducks/referralsSent";
import { closeDialog } from "../state/ducks/editDialog"; //for the consult notes modal

import useAddDocumentsToReferral from "./Referrals/useAddDocumentsToReferral";
import useRemoveDocumentFromReferral from "./Referrals/useRemoveDocumentFromReferral";

const useReferral = () => {
  const dispatch = useDispatch();
  const [suppressSnackbar, setSuppressSnackbar] = useState(false);
  const { addDocsToReferral } = useAddDocumentsToReferral();
  const { removeDocumentFromReferral } = useRemoveDocumentFromReferral();
  //Add comment to referral
  const [addCommentToReferral] = useMutation(ADD_COMMENT, {
    onCompleted: (response) => {
      if (!suppressSnackbar) {
        dispatch(
          enqueueSnackbar({
            message: `Comment saved!`,
            options: {
              variant: "success",
            },
          })
        );
      }

      const {
        description,
        isPrivate,
        createdByUser,
        createdByUserId,
        id,
        clientId,
        createTime,
      } = response.addComment;
      const { email, firstName, lastName } = createdByUser;

      const comment = {
        __typename: "Comment",
        createTime: createTime,
        createdByUser: {
          __typename: "User",
          email: email,
          firstName: firstName,
          lastName: lastName,
          clientId,
        },
        createdByUserId: createdByUserId,
        description: description,
        id: id,
        isPrivate: isPrivate,
        createdByDisplayName: firstName + " " + lastName,
        clientId,
      };

      dispatch(addCommentToReferralSuccess(comment));

      // Clear the comment text field after successful comment addition
      dispatch(resetFormCache("comment"));
    },
    onError: () => {
      dispatch(
        enqueueSnackbar({
          message: `There was an error posting your comment`,
          options: { variant: "error" },
        })
      );
    },
  });

  //update referral
  const [updateReferralMutation] = useMutation(UPDATE_REFERRAL, {
    onCompleted: (response, mutationOptions) => {
      // Allow suppressing notifications via context
      if (mutationOptions?.context?.suppressNotifications) {
        return;
      }
      // Only show default notification if no custom message is provided
      // Custom messages are handled in the wrapper function
      if (!mutationOptions?.context?.successMessage) {
        dispatch(
          enqueueSnackbar({
            message: `Referral ${response?.updateReferral?.displayableId} updated!`,
            options: {
              variant: "success",
            },
          })
        );
      }
    },
    onError: (error, mutationOptions) => {
      // Allow suppressing notifications via context
      if (mutationOptions?.context?.suppressNotifications) {
        return;
      }
      // Only show default notification if no custom message is provided
      // Custom messages are handled in the wrapper function
      if (!mutationOptions?.context?.errorMessage) {
        dispatch(
          enqueueSnackbar({
            message: `Failed while updating the referral`,
            options: { variant: "error" },
          })
        );
      }
    },
  });

  // Wrapper function to maintain backward compatibility and handle custom notifications
  const updateReferral = async (options = {}) => {
    try {
      const result = await updateReferralMutation(options);
      // Handle custom success message if provided
      if (options?.context?.successMessage) {
        dispatch(
          enqueueSnackbar({
            message: options.context.successMessage,
            options: {
              variant: "success",
            },
          })
        );
      }
      return result;
    } catch (error) {
      // Handle custom error message if provided
      if (options?.context?.errorMessage) {
        dispatch(
          enqueueSnackbar({
            message: options.context.errorMessage,
            options: { variant: "error" },
          })
        );
      }
      throw error;
    }
  };

  //update referral status
  const [updateReferralStatus, { loading: updatingReferralStatus }] =
    useMutation(SEND_REFERRAL_EVENT_MESSAGE, {
      onCompleted: (response) => {
        const { sendReferralEventMessage } = response;
        const { type, status, displayableId } = sendReferralEventMessage;
        dispatch(
          enqueueSnackbar({
            message: `Referral ${displayableId} moved to ${status}!`,
            options: { variant: "success" },
          })
        );
        dispatch(
          type === "RECEIVED"
            ? changeReferralStatusSuccessReceived(
                sendReferralEventMessage,
                status
              )
            : changeReferralStatusSuccessSent(sendReferralEventMessage, status)
        );
      },
      onError: (e) => {
        dispatch(
          enqueueSnackbar({
            message: `Error moving referral.`,
            options: { variant: "error" },
          })
        );
      },
    });

  const moveReferralFromConsultedToDone = async (
    clientId,
    processInstanceId,
    messageName,
    comment = null,
    consultNotes = null
  ) => {
    try {
      if (consultNotes?.trim()) {
        await addCommentToReferral({
          variables: {
            clientId,
            processInstanceId,
            comment: consultNotes.trim(),
            isPrivate: false,
          },
        });
      }

      if (comment?.trim()) {
        await addCommentToReferral({
          variables: {
            clientId,
            processInstanceId,
            comment: comment.trim(),
            isPrivate: false,
          },
        });
      }

      await updateReferralStatus({
        variables: {
          clientId,
          processInstanceId,
          message: { messageName },
        },
      });

      dispatch(closeDialog());
    } catch (error) {
      console.error("Error moving referral from consulted to done:", error);
      //Any error that happens on a graphql mutation will be handled by their respective onError function
    }
  };

  const rejectReferralWithComment = async (
    clientId,
    processInstanceId,
    messageName,
    comment = null
  ) => {
    setSuppressSnackbar(true);
    try {
      await addCommentToReferral({
        variables: {
          clientId,
          processInstanceId,
          comment: comment.trim(),
          isPrivate: false,
        },
        context: {
          suppressSnackbar: true,
        },
      });
      await updateReferralStatus({
        variables: {
          clientId,
          processInstanceId,
          message: { messageName },
        },
      });
      setSuppressSnackbar(false);
    } catch (error) {
      console.error("Error rejecting referral with comment:", error);
      //Any error that happens on a graphql mutation will be handled by their respective onError function
    }
  };

  const moveReferralFromScheduledToScheduling = async (
    clientId,
    processInstanceId,
    messageName,
    updatedReferral
  ) => {
    try {
      await updateReferralMutation({
        variables: {
          clientId,
          processInstanceId,
          referral: updatedReferral,
        },
      });
      await updateReferralStatus({
        variables: {
          clientId,
          processInstanceId,
          message: { messageName },
        },
      });
    } catch (error) {
      console.error(
        "Error moving referral from scheduled to scheduling:",
        error
      );
      //Any error that happens on a graphql mutation will be handled by their respective onError function
    }
  };

  return {
    addCommentToReferral,
    updateReferral,
    updateReferralStatus,
    updatingReferralStatus,
    moveReferralFromConsultedToDone,
    moveReferralFromScheduledToScheduling,
    rejectReferralWithComment,
    addDocsToReferral,
    removeDocumentFromReferral,
  };
};

export default useReferral;
